// src/pages/CreatePage.tsx - Final version with example responses and celebration context
import React, { useState, useEffect } from 'react'
import { useLocation } from 'wouter'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { Textarea } from '@/components/ui/Input'
import { useSessionStore } from '@/stores/sessionStore'
import { 
  Sparkles, 
  MessageCircle, 
  Users, 
  HelpCircle, 
  Palette, 
  ArrowLeft, 
  PartyPopper,
  ChevronLeft,
  ChevronDown,
  Star,
  Heart,
  Lightbulb,
  Save,
  Volume2,
  Mic,
  CheckCircle
} from 'lucide-react'

const CreatePage: React.FC = () => {
  const [selectedPrompt, setSelectedPrompt] = useState<string | null>(null)
  const [response, setResponse] = useState('')
  const [scaffoldingMode, setScaffoldingMode] = useState<'starters' | 'questions' | 'free' | null>(null)
  const [showMorePrompts, setShowMorePrompts] = useState(false)
  const [isSpeaking, setIsSpeaking] = useState(false)
  const [, setLocation] = useLocation()

  // Zustand store - now includes story reflections
  const { currentBrainState, setCreativeResponse, storyReflections } = useSessionStore()
  const [prefillData, setPrefillData] = useState<any>(null)

  useEffect(() => {
    const saved = localStorage.getItem('create-page-prefill')
    if (saved) {
      const data = JSON.parse(saved)
      setPrefillData(data)
      setResponse(data.response)
      setSelectedPrompt(data.promptType)
      localStorage.removeItem('create-page-prefill')
    }
  }, [])

  const concretePrompts = [
    {
      id: 'list-learned',
      title: 'What did you learn?',
      icon: Lightbulb,
      description: 'List 3 things from the story',
      placeholder: '1.\n2.\n3.',
      starters: ['I learned that...', 'The story taught me...', 'I discovered...'],
      example: 'I learned that dolphins use echolocation. I learned they travel in pods. I learned they can swim really fast.'
    },
    {
      id: 'favorite-part',
      title: 'Favorite moment',
      icon: Heart,
      description: 'Pick your favorite part and describe it',
      placeholder: 'My favorite part was when...',
      starters: ['I loved when...', 'The best part was...', 'I enjoyed...'],
      example: 'My favorite part was when the character discovered the hidden cave. It was exciting and mysterious.'
    },
    {
      id: 'questions',
      title: 'Questions',
      icon: HelpCircle,
      description: 'What questions do you still have?',
      placeholder: 'I wonder...',
      starters: ['I wonder why...', 'I want to know...', 'I\'m curious about...'],
      example: 'I wonder why the character made that choice. What would happen next?'
    },
    {
      id: 'friend-character',
      title: 'Character friend',
      icon: Users,
      description: 'Which character would you want as a friend? Why?',
      placeholder: 'I would choose...',
      starters: ['I would be friends with...', 'I like [character] because...'],
      example: 'I would be friends with Alex because they are brave and kind. We both like exploring.'
    }
  ]

  const basePrompts = concretePrompts // Keep compatibility with existing code

  // NEW: Add reflection prompts from the story
  const reflectionPrompts = storyReflections.map((reflection, idx) => ({
    id: `reflection-${idx}`,
    title: 'Think about this...',
    icon: Lightbulb,
    description: reflection,
    placeholder: 'I think...',
    starters: ['I think...', 'In my opinion...', 'I believe...'],
    example: 'I think this shows that being curious helps you learn new things.'
  }))

  // Combine base prompts with reflection prompts
  const allPrompts = [...basePrompts, ...reflectionPrompts]

  // Adapt prompts based on brain state
  const getAdaptedPrompts = () => {
    if (!currentBrainState) return allPrompts

    switch (currentBrainState.mood) {
      case 'energetic':
        return [
          allPrompts[0],
          {
            ...allPrompts[2],
            title: 'Action time!',
            description: 'What exciting action would you take?',
            placeholder: 'If I were in this story, I would jump up and...'
          },
          ...reflectionPrompts,
          allPrompts[3]
        ]
      case 'calm':
        return [
          allPrompts[1],
          {
            ...allPrompts[0],
            title: 'What happens peacefully?',
            description: 'Continue the story with a calm moment',
            placeholder: 'The character sat quietly and...'
          },
          ...reflectionPrompts,
          allPrompts[3]
        ]
      case 'focused':
      default:
        return allPrompts
    }
  }

  const adaptedPrompts = getAdaptedPrompts()

  const handlePromptSelect = (promptId: string) => {
    setSelectedPrompt(promptId)
    const prompt = adaptedPrompts.find(p => p.id === promptId)
    if (prompt) {
      setResponse('')
    }

    // Announce selection for screen readers
    const announcement = `Selected creative prompt: ${prompt?.title}`
    const announcer = document.getElementById('accessibility-announcements')
    if (announcer) {
      announcer.textContent = announcement
    }
  }

  const handleContinue = () => {
    if (selectedPrompt) {
      // Allow proceeding even with no text - some kids prefer to skip
      const hasEngaged = response.trim().length > 0
      const selectedPromptInfo = adaptedPrompts.find(p => p.id === selectedPrompt)
      
      // Determine what kind of response they created for specific praise
      let creationType = 'creative response'
      if (selectedPrompt === 'questions' || selectedPrompt.includes('reflection')) {
        creationType = 'thoughtful questions'
      } else if (selectedPrompt === 'list-learned') {
        creationType = 'learning summary'
      } else if (selectedPrompt === 'favorite-part') {
        creationType = 'story reflection'
      } else if (selectedPrompt === 'friend-character') {
        creationType = 'character connection'
      }
      
      const creativeResponse = {
        prompt: selectedPrompt,
        promptTitle: selectedPromptInfo?.title || 'Creative prompt',
        response: response.trim(),
        scaffoldingMode: scaffoldingMode,
        creationType: creationType,
        timestamp: new Date().toISOString()
      }
      
      setCreativeResponse(creativeResponse)
      setLocation('/celebrate')
    }
  }

  const handleStuckHelp = () => {
    const currentPromptData = adaptedPrompts.find(p => p.id === selectedPrompt)
    if (currentPromptData && 'starters' in currentPromptData && currentPromptData.starters) {
      const starter = currentPromptData.starters[Math.floor(Math.random() * currentPromptData.starters.length)]
      setResponse(starter + ' ')
    } else {
      // Fallback to any concrete prompt
      const prompt = concretePrompts[Math.floor(Math.random() * concretePrompts.length)]
      const starter = prompt.starters[Math.floor(Math.random() * prompt.starters.length)]
      setResponse(starter + ' ')
    }
  }

  const handleSpeakResponse = () => {
    if ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window) {
      const SpeechRecognition = (window as any).webkitSpeechRecognition || (window as any).SpeechRecognition
      const recognition = new SpeechRecognition()
      recognition.continuous = true
      recognition.onresult = (event: any) => {
        const transcript = event.results[event.results.length - 1][0].transcript
        setResponse(prev => prev + transcript + ' ')
      }
      recognition.start()
      setIsSpeaking(true)
    }
  }

  const handleSaveForLater = () => {
    localStorage.setItem('saved-create-response', JSON.stringify({
      prompt: selectedPrompt,
      response,
      scaffoldingMode,
      timestamp: new Date().toISOString()
    }))
    setLocation('/child-dashboard')
  }

  const handleReadBack = () => {
    const utterance = new SpeechSynthesisUtterance(response)
    window.speechSynthesis.speak(utterance)
  }
  
  const selectedPromptData = adaptedPrompts.find(p => p.id === selectedPrompt)

  return (
    <div className="min-h-screen bg-[#FAF9F6]">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8 lg:py-12">
        {/* Header */}
        <div className="text-center mb-6 sm:mb-8">
          <h1 className="text-2xl sm:text-3xl lg:text-4xl xl:text-5xl font-bold text-primary mb-4 flex flex-col sm:flex-row items-center justify-center gap-2 sm:gap-4">
            <span>Time to Create!</span>
            <Palette className="w-8 h-8 sm:w-10 sm:h-10 lg:w-12 lg:h-12 text-primary" />
          </h1>
          <p className="text-base sm:text-lg lg:text-xl text-muted-foreground leading-relaxed max-w-2xl mx-auto px-4">
            You've read an amazing story! Now it's your turn to add your own thoughts and ideas. 
            Pick what feels right to you - there are no wrong answers!
          </p>
        </div>

        {!scaffoldingMode ? (
          /* MODE SELECTION - happens before prompts */
          <div className="space-y-6">
            <Card className="border-primary/20 bg-primary/5">
              <CardHeader>
                <CardTitle className="text-xl">How do you want to create today?</CardTitle>
                <CardDescription>Pick the way that feels most comfortable</CardDescription>
              </CardHeader>
              <CardContent className="grid gap-4 md:grid-cols-3">
                <Card
                  className="cursor-pointer transition-all duration-300 hover:scale-[1.02] border-2 border-transparent hover:border-[#10B981] active:scale-[0.98] min-h-[120px]"
                  onClick={() => { setScaffoldingMode('starters'); setSelectedPrompt(null) }}
                >
                  <CardContent className="p-6 flex flex-col items-center justify-center text-center h-full">
                    <div className="text-4xl mb-3">✨</div>
                    <h3 className="text-xl font-bold text-[#2D3748] mb-2">Sentence Starters</h3>
                    <p className="text-sm text-[#6B7280]">Pick a starter and complete it</p>
                    <span className="text-xs text-[#10B981] font-semibold mt-2">EASIEST</span>
                  </CardContent>
                </Card>
                
                <Card
                  className="cursor-pointer transition-all duration-300 hover:scale-[1.02] border-2 border-transparent hover:border-[#2563EB] active:scale-[0.98] min-h-[120px]"
                  onClick={() => { setScaffoldingMode('questions'); setSelectedPrompt(null) }}
                >
                  <CardContent className="p-6 flex flex-col items-center justify-center text-center h-full">
                    <div className="text-4xl mb-3">🤔</div>
                    <h3 className="text-xl font-bold text-[#2D3748] mb-2">Answer Questions</h3>
                    <p className="text-sm text-[#6B7280]">Respond to specific questions</p>
                  </CardContent>
                </Card>
                
                <Card
                  className="cursor-pointer transition-all duration-300 hover:scale-[1.02] border-2 border-transparent hover:border-[#8B5CF6] active:scale-[0.98] min-h-[120px]"
                  onClick={() => { setScaffoldingMode('free'); setSelectedPrompt(null) }}
                >
                  <CardContent className="p-6 flex flex-col items-center justify-center text-center h-full">
                    <div className="text-4xl mb-3">📝</div>
                    <h3 className="text-xl font-bold text-[#2D3748] mb-2">Free Write</h3>
                    <p className="text-sm text-[#6B7280]">Write whatever you want</p>
                  </CardContent>
                </Card>
                </CardContent>
            </Card>
            
            {/* Exit option - Skip creative response entirely */}
            <div className="text-center mt-6">
              <Button
                onClick={() => setLocation('/practice-reading')}
                variant="ghost"
                className="text-sm text-[#6B7280] hover:text-[#2D3748]"
              >
                I'm done reading for now →
              </Button>
            </div>
          </div>
        ) : !selectedPrompt ? (
          /* PROMPT SELECTION SCREEN */
          <div className="space-y-6 sm:space-y-8">
            {/* Back to mode selection */}
            <Button
              onClick={() => setScaffoldingMode(null)}
              variant="outline"
              className="flex items-center gap-2"
            >
              <ChevronLeft className="w-4 h-4" />
              Change how I want to create
            </Button>

            {/* Unified Prompts - Max 3 total */}
            <div>
              <h2 className="text-lg font-medium text-[#2D3748] mb-4">
                Pick what feels right to you:
              </h2>
              
              {/* Featured Prompt - Always show first */}
              <Card 
                className="cursor-pointer mb-4 border-2 border-[#10B981] bg-[#D1FAE5] hover:shadow-xl hover:scale-[1.01] transition-all duration-300 active:scale-[0.99] min-h-[200px]"
                onClick={() => handlePromptSelect(adaptedPrompts[0].id)}
              >
                <CardContent className="p-6 flex flex-col items-center justify-center text-center">
                  {/* Featured badge at top */}
                  <div className="mb-3">
                    <span className="text-xs font-semibold text-[#059669] bg-white px-3 py-1 rounded-full border border-[#10B981]">
                      ⭐ FEATURED
                    </span>
                  </div>
                  
                  {/* Icon/Emoji */}
                  {(() => {
                    const IconComponent = adaptedPrompts[0].icon
                    return (
                      <div className="mb-3 flex justify-center">
                        <IconComponent className="w-12 h-12 text-[#10B981]" />
                      </div>
                    )
                  })()}
                  
                  {/* Title */}
                  <h3 className="text-2xl font-bold text-[#2D3748] mb-3">
                    {adaptedPrompts[0].title}
                  </h3>
                  
                  {/* Example */}
                  {'example' in adaptedPrompts[0] && adaptedPrompts[0].example && (
                    <div className="bg-white p-3 rounded-lg mb-3 w-full border border-[#10B981]">
                      <p className="text-xs text-[#059669] font-semibold mb-1">Example:</p>
                      <p className="text-sm text-[#2D3748] italic">{adaptedPrompts[0].example}</p>
                    </div>
                  )}
                  
                  {/* Description */}
                  <p className="text-sm text-[#6B7280]">
                    {adaptedPrompts[0].description}
                  </p>
                </CardContent>
              </Card>

              {/* Other Options - Smaller cards */}
              <p className="text-sm text-[#6B7280] mb-2">Or try one of these:</p>
              <div className="grid gap-3 md:grid-cols-2">
                {adaptedPrompts.slice(1, 3).map((prompt) => (
                  <Card 
                    key={prompt.id}
                    className="cursor-pointer transition-all duration-300 hover:scale-[1.02] border-2 border-transparent hover:border-[#10B981] hover:shadow-md active:scale-[0.98] min-h-[140px]"
                    onClick={() => handlePromptSelect(prompt.id)}
                  >
                    <CardContent className="p-4 flex flex-col items-center justify-center text-center h-full">
                      {/* Icon */}
                      <prompt.icon className="w-8 h-8 text-[#6B7280] mb-2" />
                      
                      {/* Title */}
                      <h4 className="text-base font-semibold text-[#2D3748] mb-1">
                        {prompt.title}
                      </h4>
                      
                      {/* Brief description */}
                      <p className="text-xs text-[#6B7280] line-clamp-2">
                        {prompt.description}
                      </p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>

            {/* Encouragement */}
            <div className="text-center">
              <div className="inline-flex items-center gap-2 px-6 py-3 bg-white/60 rounded-full border border-primary/20">
                <PartyPopper className="w-5 h-5 text-primary" />
                <p className="text-sm sm:text-base text-muted-foreground font-medium">
                  Choose what feels good right now. 
                  There's no right or wrong way to be creative.
                </p>
              </div>
            </div>

            {/* Exit option */}
            <div className="text-center mt-4">
              <Button
                onClick={() => setLocation('/practice-reading')}
                variant="ghost"
                className="text-sm text-[#6B7280] hover:text-[#2D3748]"
              >
                Skip writing for now →
              </Button>
            </div>
          </div>
        ) : (
          /* WRITING SCREEN */
          <div className="space-y-6 sm:space-y-8">
            {/* Selected Prompt Info */}
            <Card className="border-primary/20 bg-primary/5">
              <CardHeader className="pb-4">
                <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
                  <div className="p-2 bg-primary/10 rounded-lg">
                    {selectedPromptData && <selectedPromptData.icon className="w-5 h-5 sm:w-6 sm:h-6 text-primary" />}
                  </div>
                  <div className="flex-1">
                    <CardTitle className="text-lg text-[#2D3748] mb-2">
                      {selectedPromptData?.title}
                    </CardTitle>
                    {/* Example FIRST - bigger */}
                    {selectedPromptData && 'example' in selectedPromptData && selectedPromptData.example && (
                      <div className="bg-[#D1FAE5] p-3 rounded-lg mb-2 border border-[#10B981]">
                        <p className="text-xs text-[#059669] font-semibold mb-1">Here's an example:</p>
                        <p className="text-sm text-[#2D3748]">{selectedPromptData.example}</p>
                      </div>
                    )}
                    <CardDescription className="text-sm text-[#6B7280]">
                      {selectedPromptData?.description}
                    </CardDescription>
                  </div>
                </div>
              </CardHeader>
            </Card>

            {/* Writing Area */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg sm:text-xl">Your Creative Response</CardTitle>
                <CardDescription className="text-sm sm:text-base">
                  Write as much or as little as you want. Take your time!
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <Textarea
                  value={response}
                  onChange={(e) => setResponse(e.target.value)}
                  placeholder={selectedPromptData?.placeholder || 'Start writing here...'}
                  className="min-h-[300px] text-lg leading-relaxed resize-none focus:ring-2 focus:ring-[#10B981] p-4"
                  autoFocus
                />
                
                {/* Support Buttons - Minimal */}
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={() => handleStuckHelp()}>
                    <HelpCircle className="w-4 h-4 mr-2" />
                    Help me start
                  </Button>
                  
                  {response && (
                    <Button variant="outline" size="sm" onClick={() => handleReadBack()}>
                      <Volume2 className="w-4 h-4 mr-2" />
                      Read it back
                    </Button>
                  )}
                </div>
                
                <div className="text-sm text-muted-foreground text-center">
                  ✨ Any amount of writing is great! Even just a few words.
                </div>

                {/* Action Buttons */}
                <div className="flex flex-col sm:flex-row gap-3">
                  <Button
                    onClick={() => setSelectedPrompt(null)}
                    variant="outline"
                    className="flex items-center gap-2"
                  >
                    <ChevronLeft className="w-4 h-4" />
                    Choose Different Prompt
                  </Button>
                  
                  <Button
                    onClick={handleContinue}
                    className="flex items-center gap-2 flex-1 bg-[#10B981] hover:bg-[#059669] text-white"
                  >
                    {response.trim().length > 0 ? "I'm Done!" : "Skip for now →"}
                    <CheckCircle className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Hidden announcements area for screen readers */}
        <div id="accessibility-announcements" className="sr-only" aria-live="polite"></div>
      </div>
    </div>
  )
}

export default CreatePage